"use client";

import React, { useCallback } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import { Switch, Text } from "@lmn/design-system";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";

const FilterWrapper = styled.div<{ $onWhiteBackground?: boolean }>`
  width: 100%;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: ${({ $onWhiteBackground, theme }) =>
    $onWhiteBackground
      ? theme.kallisto.color.background.static.backgroundLayer01
      : theme.kallisto.color.background.static.backgroundPage};
  padding: 1rem;
  border-radius: 8px;

  & label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: 0;
  }

  & label > span {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

interface FlexibleCancellationFilterProps {
  delayedUpdate?: boolean;
  onWhiteBackground?: boolean;
}

const FlexibleCancellationFilter: React.FC<FlexibleCancellationFilterProps> = ({
  delayedUpdate = false,
  onWhiteBackground = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilter } = useFilterState();

  // Derive state directly from URL/tempFilters - no local state needed
  const isFlexibleCancellation = delayedUpdate
    ? tempFilters.get("flexibleOffers") === "flexibleCancellation"
    : searchParams.get("flexibleOffers") === "flexibleCancellation";

  const handleFlexibleCancellationChange = useCallback(
    (checked: boolean) => {
      if (delayedUpdate) {
        updateTempFilter(
          "flexibleOffers",
          checked ? "flexibleCancellation" : null
        );
      } else {
        updateFilter("flexibleOffers", checked ? "flexibleCancellation" : null);
      }
    },
    [updateFilter, updateTempFilter, delayedUpdate]
  );

  return (
    <FilterWrapper $onWhiteBackground={onWhiteBackground}>
      <Switch
        checked={isFlexibleCancellation}
        onChange={handleFlexibleCancellationChange}
        ltr={true}
        // @ts-expect-error: change this in design system so that it accept React Components
        label={
          <Label>
            <Text variant="BodySmall">Flexibel stornier - und umbuchbar</Text>
          </Label>
        }
        aria-describedby="flexible-cancellation-description"
        data-testid="flexible-cancellation-switch"
      />
      <span id="flexible-cancellation-description" className="sr-only">
        Wählen Sie aus, ob Sie nur Angebote mit flexibler Stornierung sehen
        möchten.
      </span>
    </FilterWrapper>
  );
};

export default FlexibleCancellationFilter;
